import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, useBlocker, useBeforeUnload, useLocation } from "react-router-dom";
import { Tool, Message, StartMessage, ServerConfig, SchemaProperty, HandoffDestination, TransferDestination, NumberDestination, SipDestination, AssistantDestination, StepDestination, TransferPlan } from "@/api/tools/tools.types";
import { getToolById, updateTool, createTool, deleteTool } from "@/api/tools/tools.api";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
    AlertCircle, 
    CheckCircle2,
    XCircle,
    Wrench,
    Copy,
    Settings2,
    ArrowLeft,
    Save,
    Loader2,
    ShieldAlert,
    ClipboardCheck,
    Trash2,
    Hash,            // For Tool ID display and empty states
    Download,        // For Export button
    Upload,          // For Import button
    // Tab icons
    Code2,           // Function tab
    Plug,            // Connection tab  
    MessageCircle,   // Messages tab
    PhoneCall,       // Transfer tab
    Database,        // QKnowledge tab
    Mail,            // Voicemail tab
    GitBranch,       // TransAssist tab
    // Empty state icons (old icons kept for empty states)
    MessageSquare,
    PhoneForwarded,
    Book,
    Voicemail,
    ArrowRightLeft,
    Webhook          // For Connection empty state
} from "lucide-react";
import { FadeId } from "@/components/ui/fade-id";
import { ConnectionForm } from "./components/forms/ConnectionForm";
import { SchemaForm } from "./components/forms/SchemaForm";
import { DetailsForm } from "./components/forms/DetailsForm";
import { MessagesForm } from "./components/forms/MessagesForm";
import { DeleteDialog } from "./components/DeleteDialog";
import { ToolDetailsSkeleton } from "./components/ToolDetailsSkeleton";
import { MobileOverlay } from '@/components/ui/mobile-overlay';
import { AnimatePresence, motion } from 'framer-motion';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { TransferForm } from "./components/forms/TransferForm";
import { useAuth } from "@/context/AuthContext";
import { KnowledgeBaseForm } from "./components/forms/KnowledgeBaseForm";
import { VoicemailForm } from "./components/forms/VoicemailForm";
import { HandoffForm } from "./components/forms/HandoffForm";
import { SiteHeader } from "@/components/site-header";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { downloadToolConfig } from "@/utils/toolImportExport";

interface SchemaState {
    name: string;
    type: SchemaProperty['type'];
    description: string;
    required: boolean;
    itemType?: 'string' | 'number' | 'boolean' | 'object';  // For array types - restricted to 4 types
    enum?: string[];  // For string type only
}

interface FunctionState {
    name: string;
    description: string;
    parameters: SchemaState[];
}

interface FormState {
    type: Tool['type'];
    isAsync: boolean;
}

interface ConnectionState extends Omit<ServerConfig, 'url'> {
    url: string;
    isAsync: boolean;
}

// MessagesState is now just Message[] from the API types
// Keeping this comment for reference - messages are now handled as Message[] directly

interface KnowledgeBaseState {
    name: string;
    description: string;
    fileIds: string[];
    provider: 'google';
    model?: string;
}

interface VoicemailState {
    messageContent: string;
    useAudioFile: boolean;
}

const initialFormState: FormState = {
    type: "function",
    isAsync: false
};

const initialConnectionState: ConnectionState = {
    url: "",
    timeoutSeconds: 30,
    secret: "",
    headers: {},
    isAsync: false
};

const initialFunctionState: FunctionState = {
    name: "",
    description: "",
    parameters: []
};

const initialMessagesState: Message[] = [];

const initialKnowledgeBasesState: KnowledgeBaseState[] = [];

const initialVoicemailState: VoicemailState = {
    messageContent: "",
    useAudioFile: false
};

// Define TYPE_LABELS for tab messages
const TYPE_LABELS = {
    function: "Function",
    dtmf: "DTMF",
    endCall: "End Call",
    transferCall: "Transfer",
    output: "Output",
    mcp: "MCP",
    query: "QKnowledge",
    voicemail: "Voicemail",
    handoff: "TransAssist"
} as const;

// Helper function to clean empty optional fields from TransferDestination
const cleanTransferDestination = (destination: TransferDestination): TransferDestination => {
    const cleaned = { ...destination };

    // Remove empty description (optional for all types)
    if (cleaned.description === "" || cleaned.description === undefined) {
        delete cleaned.description;
    }

    // Remove empty message (optional for all types)
    if (cleaned.message === "" || cleaned.message === undefined) {
        delete cleaned.message;
    }

    // Type-specific cleanup
    if (cleaned.type === 'number') {
        const numberDest = cleaned as NumberDestination;
        // Remove empty callerId
        if (numberDest.callerId === "" || numberDest.callerId === undefined) {
            delete numberDest.callerId;
        }
        // Remove empty extension
        if (numberDest.extension === "" || numberDest.extension === undefined) {
            delete numberDest.extension;
        }
        // Clean transferPlan if it exists
        if (numberDest.transferPlan) {
            numberDest.transferPlan = cleanTransferPlan(numberDest.transferPlan);
            // Keep transferPlan even if it only has mode and sipVerb (common default case)
            // It will only be removed if it's completely empty (shouldn't happen)
        }
        return numberDest;
    } else if (cleaned.type === 'sip') {
        const sipDest = cleaned as SipDestination;
        // Clean transferPlan if it exists
        if (sipDest.transferPlan) {
            sipDest.transferPlan = cleanTransferPlan(sipDest.transferPlan);
            // Keep transferPlan even if it only has mode and sipVerb (common default case)
            // It will only be removed if it's completely empty (shouldn't happen)
        }
        // Remove empty sipHeaders object
        if (sipDest.sipHeaders && Object.keys(sipDest.sipHeaders).length === 0) {
            delete sipDest.sipHeaders;
        }
        return sipDest;
    } else if (cleaned.type === 'assistant') {
        const assistantDest = cleaned as AssistantDestination;
        // No additional cleanup needed - description and message already handled above
        return assistantDest;
    } else if (cleaned.type === 'step') {
        const stepDest = cleaned as StepDestination;
        // No additional cleanup needed - description already handled above
        return stepDest;
    }

    return cleaned;
};

// Helper function to clean empty optional fields from TransferPlan
const cleanTransferPlan = (plan: TransferPlan): TransferPlan => {
    const cleaned: TransferPlan = {
        mode: plan.mode
    };

    // Add sipVerb if it exists (required for some modes, optional for others)
    if (plan.sipVerb !== undefined) {
        cleaned.sipVerb = plan.sipVerb;
    }

    // Add message if it's not empty
    if (plan.message !== undefined && plan.message !== "") {
        cleaned.message = plan.message;
    }

    // Add timeout if it exists
    if (plan.timeout !== undefined) {
        cleaned.timeout = plan.timeout;
    }

    // Add holdAudioUrl if it's not empty
    if (plan.holdAudioUrl !== undefined && plan.holdAudioUrl !== "") {
        cleaned.holdAudioUrl = plan.holdAudioUrl;
    }

    // Add transferCompleteAudioUrl if it's not empty
    if (plan.transferCompleteAudioUrl !== undefined && plan.transferCompleteAudioUrl !== "") {
        cleaned.transferCompleteAudioUrl = plan.transferCompleteAudioUrl;
    }

    // Add twiml if it's not empty
    if (plan.twiml !== undefined && plan.twiml !== "") {
        cleaned.twiml = plan.twiml;
    }

    // Add summaryPlan if it exists and has content
    if (plan.summaryPlan) {
        const hasSummaryContent = plan.summaryPlan.messages?.length || 
                                   plan.summaryPlan.enabled !== undefined ||
                                   plan.summaryPlan.timeoutSeconds !== undefined;
        if (hasSummaryContent) {
            cleaned.summaryPlan = { ...plan.summaryPlan };
            // Clean empty messages array
            if (cleaned.summaryPlan.messages && cleaned.summaryPlan.messages.length === 0) {
                delete cleaned.summaryPlan.messages;
            }
        }
    }

    // Add sipHeadersInReferToEnabled if it's set
    if (plan.sipHeadersInReferToEnabled !== undefined) {
        cleaned.sipHeadersInReferToEnabled = plan.sipHeadersInReferToEnabled;
    }

    // Add fallbackPlan if it exists
    if (plan.fallbackPlan) {
        cleaned.fallbackPlan = plan.fallbackPlan;
    }

    return cleaned;
};

export default function ToolDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { error, success } = useEnhancedToast();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(!!id);
    const [isSaving, setIsSaving] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const hasUnsavedChangesRef = useRef(false);
    const isNavigatingAfterCreateRef = useRef(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
    const [bypassBlocker, setBypassBlocker] = useState(false);
    const [activeTab, setActiveTab] = useState("function");
    const [key, setKey] = useState(0);
    const [tool, setTool] = useState<Tool | null>(null);

    // Create refs for toast functions
    const errorRef = useRef(error);
    const successRef = useRef(success);

    // Track initial form state
    const [initialDetailsData, setInitialDetailsData] = useState<FormState>(initialFormState);
    const [initialConnectionData, setInitialConnectionData] = useState<ConnectionState>(initialConnectionState);
    const [initialFunctionData, setInitialFunctionData] = useState<FunctionState>(initialFunctionState);
    const [initialMessagesData, setInitialMessagesData] = useState<Message[]>(initialMessagesState);
    const [initialKnowledgeBasesData, setInitialKnowledgeBasesData] = useState<KnowledgeBaseState[]>(initialKnowledgeBasesState);
    const [initialVoicemailData, setInitialVoicemailData] = useState<VoicemailState>(initialVoicemailState);

    // Update refs when toast functions change
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    // Form states
    const [connectionData, setConnectionData] = useState<ConnectionState>(initialConnectionState);
    const [functionData, setFunctionData] = useState<FunctionState>(initialFunctionState);
    const [detailsData, setDetailsData] = useState<FormState>(initialFormState);
    const [messagesData, setMessagesData] = useState<Message[]>(initialMessagesState);
    const [knowledgeBasesData, setKnowledgeBasesData] = useState<KnowledgeBaseState[]>(initialKnowledgeBasesState);
    const [voicemailData, setVoicemailData] = useState<VoicemailState>(initialVoicemailState);

    // Define the visible tabs based on tool type
    const getVisibleTabs = useCallback(() => {
        if (!detailsData || !detailsData.type) {
            return [
                { id: "function", label: "Function", icon: Code2, available: false },
                { id: "connection", label: "Connection", icon: Plug, available: false },
                { id: "messages", label: "Messages", icon: MessageCircle, available: false },
                { id: "transfer", label: "Transfer", icon: PhoneCall, available: false },
                { id: "knowledgeBases", label: "QKnowledge", icon: Database, available: false },
                { id: "voicemail", label: "Voicemail", icon: Mail, available: false },
                { id: "handoff", label: "TransAssist", icon: GitBranch, available: false }
            ];
        }
        
        // Tool type specific visibility logic
        const isVoicemail = detailsData.type === "voicemail";
        const isHandoff = detailsData.type === "handoff";
        
        return [
            { id: "function", label: "Function", icon: Code2, available: true }, // Always available - Always first
            { id: "connection", label: "Connection", icon: Plug, available: !isVoicemail && !isHandoff }, // Always second
            { id: "messages", label: "Messages", icon: MessageCircle, available: true }, // All tool types can have messages
            { id: "transfer", label: "Transfer", icon: PhoneCall, available: detailsData.type === "transferCall" },
            { id: "knowledgeBases", label: "QKnowledge", icon: Database, available: detailsData.type === "query" },
            { id: "voicemail", label: "Voicemail", icon: Mail, available: isVoicemail },
            { id: "handoff", label: "TransAssist", icon: GitBranch, available: isHandoff }
        ];
    }, [detailsData?.type]);

    const visibleTabs = getVisibleTabs();

    // Set active tab to the first available tab when tool type changes
    useEffect(() => {
        // If current active tab is not available, switch to the first available tab
        const currentTabAvailable = visibleTabs.find(tab => tab.id === activeTab)?.available;
        if (!currentTabAvailable) {
            const firstAvailableTab = visibleTabs.find(tab => tab.available);
            if (firstAvailableTab) {
                setActiveTab(firstAvailableTab.id);
            }
        }
    }, [detailsData.type, visibleTabs, activeTab]);

    // Check if user has editor access
    const hasEditorAccess = user?.permissions?.tools === 'EDITOR';

    // Check for actual changes
    const hasActualChanges = useCallback(() => {
        // For new tools, only check if any data has been entered
        if (location.pathname === '/dashboard/tools/new') {
            const hasChanges = !!(detailsData.type !== "function" || 
                     detailsData.isAsync || 
                    connectionData.url || 
                    connectionData.isAsync ||  // Check connection isAsync too
                    functionData.name || 
                    functionData.description || 
                    functionData.parameters.length > 0 || 
                    messagesData.length > 0 ||
                knowledgeBasesData.length > 0 ||
                voicemailData.messageContent ||
                (tool?.destinations && tool.destinations.length > 0));
            return hasChanges;
        }

        // For existing tools, compare with initial state
        // Stringify the objects in a consistent way to avoid false positives
        const sortAndStringify = <T extends object>(obj: T) => JSON.stringify(obj, Object.keys(obj).sort());

        const detailsChanged = sortAndStringify(detailsData) !== sortAndStringify(initialDetailsData);
        const connectionChanged = sortAndStringify(connectionData) !== sortAndStringify(initialConnectionData);
        const functionChanged = sortAndStringify(functionData) !== sortAndStringify(initialFunctionData);
        const messagesChanged = sortAndStringify(messagesData) !== sortAndStringify(initialMessagesData);
        const knowledgeBasesChanged = sortAndStringify(knowledgeBasesData) !== sortAndStringify(initialKnowledgeBasesState);
        const voicemailChanged = sortAndStringify(voicemailData) !== sortAndStringify(initialVoicemailData);
        const destinationsChanged = !!(tool?.destinations && tool.destinations.length > 0);

        // Additionally check if async has changed in either form
        const asyncChanged = detailsData.isAsync !== initialDetailsData.isAsync || 
                             connectionData.isAsync !== initialConnectionData.isAsync;

        return detailsChanged || connectionChanged || functionChanged || messagesChanged || 
               knowledgeBasesChanged || voicemailChanged || destinationsChanged || asyncChanged;
    }, [detailsData, connectionData, functionData, messagesData, knowledgeBasesData, voicemailData,
        initialDetailsData, initialConnectionData, initialFunctionData, initialMessagesData, 
        initialKnowledgeBasesState, initialVoicemailData, location.pathname, tool]);

    useEffect(() => {
        const fetchTool = async () => {
            try {
                const response = await getToolById(id || "");
                if (response.error) throw new Error(response.error);
                if (!response.data) throw new Error("No data returned from server");

                const data = response.data;
                setTool(data);

                // Update details form with non-null values
                const details: FormState = {
                    type: data.type,
                    isAsync: Boolean(data.async)
                };
                setDetailsData(details);
                setInitialDetailsData(details);

                // Update connection form with non-null values
                const connection: ConnectionState = {
                    url: data.server?.url || "",
                    timeoutSeconds: data.server?.timeoutSeconds || 30,
                    secret: data.server?.secret || "",
                    headers: data.server?.headers || {},
                    isAsync: Boolean(data.async)
                };
                setConnectionData(connection);
                setInitialConnectionData(connection);

                // Update function form with non-null values
                const properties = data.function?.parameters?.properties || {};
                const required = data.function?.parameters?.required || [];
                
                const functionState: FunctionState = {
                    name: data.function?.name || "",
                    description: data.function?.description || "",
                    parameters: Object.entries(properties).map(([key, value]) => ({
                        name: key,
                        type: value.type,
                        description: value.description || "",
                        required: required.includes(key),
                        itemType: value.items?.type as 'string' | 'number' | 'boolean' | 'object' || 'string',
                        enum: value.enum || []
                    }))
                };
                
                setFunctionData(functionState);
                setInitialFunctionData(functionState);

                // Update messages form with non-null values
                const messages = data.messages || [];
                setMessagesData(messages);
                setInitialMessagesData(messages);

                // Update knowledgeBases form with non-null values
                if (data.knowledgeBases && data.knowledgeBases.length > 0) {
                    setKnowledgeBasesData(data.knowledgeBases);
                }

                // Update voicemail form with non-null values for voicemail tools
                if (data.type === "voicemail") {
                    const startMessage = messages.find(m => m.type === "request-start");
                    const messageContent = startMessage?.content || "";
                    const useAudioFile = messageContent.startsWith('http://') || messageContent.startsWith('https://');
                    
                    const voicemailState: VoicemailState = {
                        messageContent,
                        useAudioFile
                    };
                    setVoicemailData(voicemailState);
                    setInitialVoicemailData(voicemailState);
                }
            } catch (error) {
                console.error("Error fetching tool:", error);
                errorRef.current({
                    icon: AlertCircle,
                    title: "Failed to Load Tool",
                    description: error instanceof Error ? error.message : "Could not fetch tool details",
                    duration: 4000,
                });
                navigate("/dashboard/tools");
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            fetchTool();
        } else {
            console.log('[New Tool] useEffect triggered. Location state:', location.state); // Log 1
            
            // Check if we are navigating from an import action
            const importedToolData = location.state?.importedToolData as Partial<Tool> | null;
            
            // Check if we are navigating from a duplication action
            const duplicatedToolData = location.state?.duplicatedToolData as {
                details: FormState;
                connection: ConnectionState;
                function: FunctionState;
                messages: Message[];
                destinations: Tool['destinations'];
            } | null;

            if (importedToolData) {
                console.log('[New Tool] Found importedToolData:', importedToolData);
                
                // Convert imported tool data to form states
                const details: FormState = {
                    type: importedToolData.type || "function",
                    isAsync: Boolean(importedToolData.async)
                };
                setDetailsData(details);
                setInitialDetailsData(details);

                // Connection data
                const connection: ConnectionState = {
                    url: importedToolData.server?.url || "",
                    timeoutSeconds: importedToolData.server?.timeoutSeconds || 30,
                    secret: "", // Always empty for imported data
                    headers: importedToolData.server?.headers || {},
                    isAsync: Boolean(importedToolData.async)
                };
                setConnectionData(connection);
                setInitialConnectionData(connection);

                // Function data
                const properties = importedToolData.function?.parameters?.properties || {};
                const required = importedToolData.function?.parameters?.required || [];
                
                const functionState: FunctionState = {
                    name: importedToolData.function?.name || "",
                    description: importedToolData.function?.description || "",
                    parameters: Object.entries(properties).map(([key, value]) => ({
                        name: key,
                        type: value.type,
                        description: value.description || "",
                        required: required.includes(key),
                        itemType: value.items?.type as 'string' | 'number' | 'boolean' | 'object' || 'string',
                        enum: value.enum || []
                    }))
                };
                setFunctionData(functionState);
                setInitialFunctionData(functionState);

                // Messages data
                const messages = importedToolData.messages || [];
                setMessagesData(messages);
                setInitialMessagesData(messages);

                // Knowledge bases data
                if (importedToolData.knowledgeBases && importedToolData.knowledgeBases.length > 0) {
                    setKnowledgeBasesData(importedToolData.knowledgeBases);
                    setInitialKnowledgeBasesData(importedToolData.knowledgeBases);
                }

                // Voicemail data (for voicemail tools)
                if (importedToolData.type === "voicemail") {
                    const startMessage = messages.find(m => m.type === "request-start");
                    const messageContent = startMessage?.content || "";
                    const useAudioFile = messageContent.startsWith('http://') || messageContent.startsWith('https://');
                    
                    const voicemailState: VoicemailState = {
                        messageContent,
                        useAudioFile
                    };
                    setVoicemailData(voicemailState);
                    setInitialVoicemailData(voicemailState);
                }

                // Set the tool state
                setTool({
                    id: "",
                    type: details.type,
                    async: details.isAsync,
                    function: importedToolData.function || null,
                    server: connection.url ? {
                        url: connection.url,
                        timeoutSeconds: connection.timeoutSeconds,
                        secret: connection.secret,
                        headers: connection.headers
                    } : null,
                    messages: messages,
                    destinations: importedToolData.destinations || [],
                    knowledgeBases: importedToolData.knowledgeBases,
                    createdAt: new Date().toISOString()
                });

            } else if (duplicatedToolData) {
                console.log('[New Tool] Found duplicatedToolData:', duplicatedToolData); // Log 2
                // Initialize state from duplicated data
                const { details, connection, function: funcData, messages, destinations } = duplicatedToolData;

                // Update details form
                setDetailsData(details);
                setInitialDetailsData(details);

                // Update connection form
                setConnectionData(connection);
                setInitialConnectionData(connection);

                // Update function form (append " (Copy)" to name)
                const duplicatedFunctionData = {
                    ...funcData,
                    name: `${funcData.name} (Copy)`
                };
                setFunctionData(duplicatedFunctionData);
                setInitialFunctionData(duplicatedFunctionData);

                // Update messages form
                setMessagesData(messages);
                setInitialMessagesData(messages);

                // Log state after setting
                console.log('[New Tool] State after setting from duplicate:', {
                    details: detailsData, // Log current state, not the duplicate variable
                    connection: connectionData,
                    function: functionData,
                    messages: messagesData
                }); // Log 3

                // Set the tool state with duplicated data, generating a new ID placeholder if needed
                setTool({
                    id: "", // New tool doesn't have an ID yet
                    type: details.type,
                    async: details.isAsync,
                    function: !duplicatedFunctionData.name.trim() ? null : {
                        name: duplicatedFunctionData.name,
                        description: duplicatedFunctionData.description,
                        parameters: {
                            type: "object",
                            properties: duplicatedFunctionData.parameters.reduce((acc: Record<string, SchemaProperty>, prop) => {
                                acc[prop.name] = { type: prop.type, description: prop.description };
                                return acc;
                            }, {}),
                            required: duplicatedFunctionData.parameters.filter(p => p.required).map(p => p.name)
                        }
                    },
                    server: connection.url ? {
                        url: connection.url,
                        timeoutSeconds: connection.timeoutSeconds,
                        secret: connection.secret,
                        headers: connection.headers
                    } : null,
                    messages: messages,
                    destinations: destinations,
                    createdAt: new Date().toISOString()
                });

                // Clear the location state after using it
                // console.log('[New Tool] Clearing location state.'); // Log 4 - Commented out log
                // navigate(location.pathname, { replace: true, state: null }); // REMOVED THIS LINE

            } else {
                console.log('[New Tool] No duplicatedToolData found. Resetting state.'); // Log 5
                // Reset all states for a brand new tool (not duplicated)
                setDetailsData(initialFormState);
                setConnectionData(initialConnectionState);
                setFunctionData(initialFunctionState);
                setMessagesData(initialMessagesState);
                setInitialDetailsData(initialFormState);
                setInitialConnectionData(initialConnectionState);
                setInitialFunctionData(initialFunctionState);
                setInitialMessagesData(initialMessagesState);
                setInitialKnowledgeBasesData(initialKnowledgeBasesState);
                setTool({
                    id: "",
                    type: "function",
                    async: false,
                    function: null,
                    server: null,
                    messages: [],
                    destinations: [],
                    createdAt: new Date().toISOString()
                });
            }
            setIsLoading(false);
        }
    }, [id, navigate, location.state, location.pathname]);

    // Form state handlers
    const handleDetailsChange = (data: FormState) => {
        if (!hasEditorAccess) return;
        
        // If tool type is changing, update the tool state too
        if (data.type !== detailsData.type && tool) {
            setTool({
                ...tool,
                type: data.type
            });
            
            // If changing to voicemail and no function name set, set defaults
            if (data.type === "voicemail" && !functionData.name) {
                setFunctionData({
                    name: "leave_voicemail",
                    description: "Leave a voicemail message when you detect you've reached a voicemail system",
                    parameters: []
                });
            }
            
            // If changing to handoff and no function name set, set defaults
            if (data.type === "handoff" && !functionData.name) {
                setFunctionData({
                    name: "transassist_tool",
                    description: "Transfer assistance to another assistant or destination",
                    parameters: []
                });
            }
        }
        
        // If isAsync is changing, sync to connectionData
        if (data.isAsync !== detailsData.isAsync) {
            setConnectionData({
                ...connectionData,
                isAsync: data.isAsync
            });
        }
        
        setDetailsData(data);
        handleFormUpdate();
    };

    const handleConnectionChange = (data: ConnectionState) => {
        if (!hasEditorAccess) return;
        
        // If isAsync has changed, update the details data too
        if (data.isAsync !== detailsData.isAsync) {
            setDetailsData({
                ...detailsData,
                isAsync: data.isAsync
            });
        }
        
        setConnectionData(data);
        handleFormUpdate();
    };

    const handleFunctionChange = (data: FunctionState) => {
        if (!hasEditorAccess) return;
        setFunctionData(data);
        handleFormUpdate();
    };

    const handleMessagesChange = (data: Message[]) => {
        if (!hasEditorAccess) return;
        setMessagesData(data);
        handleFormUpdate();
    };

    const handleKnowledgeBasesChange = (data: KnowledgeBaseState[]) => {
        if (!hasEditorAccess) return;
        setKnowledgeBasesData(data);
        handleFormUpdate();
    };

    const handleVoicemailChange = (data: VoicemailState) => {
        if (!hasEditorAccess) return;
        setVoicemailData(data);
        handleFormUpdate();
    };

    // Sync ref whenever state changes
    useEffect(() => {
        hasUnsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    // Reset navigation flag when component mounts/unmounts or route changes
    useEffect(() => {
        // Reset the flag when we navigate to a different page (with or without id)
        isNavigatingAfterCreateRef.current = false;
    }, [id]);

    // Track changes with debounce
    const handleFormUpdate = useCallback(() => {
        const hasChanges = hasActualChanges();
        // Only update if actually changed to prevent infinite loops
        if (hasUnsavedChanges !== hasChanges) {
            setHasUnsavedChanges(hasChanges);
        }
    }, [hasActualChanges, hasUnsavedChanges]);

    // Prevent accidental navigation
    useBeforeUnload(
        useCallback(
            (event) => {
                if (hasUnsavedChangesRef.current && !isNavigatingAfterCreateRef.current) {
                    event.preventDefault();
                    return event.returnValue = "You have unsaved changes. Are you sure you want to leave?";
                }
            },
            []
        )
    );

    // Block navigation and show dialog - use simpler logic with refs
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }: { currentLocation: { pathname: string }, nextLocation: { pathname: string } }) => {
            // Don't block if navigating after create
            if (isNavigatingAfterCreateRef.current) {
                return false;
            }
            // Block if we have unsaved changes and moving to different path
            return hasUnsavedChangesRef.current && 
                   currentLocation.pathname !== nextLocation.pathname;
        }
    );

    // Update the handleCancel function
    const handleCancel = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.reset();
        }
        setTimeout(() => {
            setShowUnsavedDialog(false);
            setPendingAction(null);
            setBypassBlocker(true);
            setTimeout(() => {
                setBypassBlocker(false);
            }, 100);
        }, 0);
    }, [blocker]);

    // Update the blocker effect
    useEffect(() => {
        if (blocker.state === "blocked" && !showUnsavedDialog && !isNavigatingAfterCreateRef.current) {
            setShowUnsavedDialog(true);
            setPendingAction(() => () => {
                setBypassBlocker(true);
                blocker.proceed();
            });
        }
    }, [blocker, showUnsavedDialog]);

    const handleBack = () => {
        if (hasUnsavedChanges && !showUnsavedDialog) {
            setShowUnsavedDialog(true);
            setPendingAction(() => () => {
                setBypassBlocker(true);
                navigate('/dashboard/tools');
            });
        } else {
            navigate('/dashboard/tools');
        }
    };

    const handleSave = async () => {
        if (!hasEditorAccess) {
            errorRef.current({
                icon: AlertCircle,
                title: "Permission Denied",
                description: "You need editor permissions to save changes",
                duration: 4000,
            });
            return;
        }

        setIsSaving(true);
        setBypassBlocker(true);

        try {
            // Convert schema array to properties object
            const schemaProperties = functionData.parameters.reduce((acc: Record<string, SchemaProperty>, prop) => {
                const property: SchemaProperty = {
                    type: prop.type,
                    description: prop.description
                };
                
                // Add items field for array types
                if (prop.type === 'array' && prop.itemType) {
                    property.items = {
                        type: prop.itemType
                    };
                }
                
                // Add enum field for string types (only if not empty)
                if (prop.type === 'string' && prop.enum && prop.enum.length > 0) {
                    // Filter out empty strings from enum values
                    const filteredEnum = prop.enum.filter(value => value.trim() !== '');
                    if (filteredEnum.length > 0) {
                        property.enum = filteredEnum;
                    }
                }
                
                acc[prop.name] = property;
                return acc;
            }, {});

            let toolMessages: Message[] = [];
            
            // For voicemail tools, use voicemail message data
            if (detailsData.type === "voicemail" && voicemailData.messageContent) {
                toolMessages.push({
                    type: "request-start",
                    content: voicemailData.messageContent
                } as StartMessage);
            } else {
                // For other tools, use messages array directly
                toolMessages = messagesData.filter(msg => msg.content || msg.conditions?.length);
            }

            // Prepare the tool data
            const toolData: Partial<Tool> = {
                id: tool?.id,
                type: detailsData.type,
                async: detailsData.isAsync,
                messages: toolMessages,
                server: connectionData.url ? {
                    url: connectionData.url,
                    timeoutSeconds: connectionData.timeoutSeconds,
                    secret: connectionData.secret,
                    headers: connectionData.headers
                } : null,
                knowledgeBases: knowledgeBasesData.length > 0 ? knowledgeBasesData : undefined
            };

            // Add function details if they exist, for any tool type
            if (functionData.name) {
                toolData.function = {
                    name: functionData.name,
                    description: functionData.description,
                    parameters: functionData.parameters.length > 0 ? {
                        type: "object",
                        properties: schemaProperties,
                        required: functionData.parameters.filter(p => p.required).map(p => p.name)
                    } : undefined
                };
            }

            // Add destinations for transfer and handoff tools
            if ((detailsData.type === "transferCall" || detailsData.type === "handoff") && tool?.destinations) {
                // Clean empty optional fields from destinations
                if (detailsData.type === "transferCall") {
                    toolData.destinations = (tool.destinations as TransferDestination[]).map(cleanTransferDestination);
                } else {
                    // For handoff tools, keep destinations as-is (they have different structure)
                    toolData.destinations = tool.destinations;
                }
            }

            // console.log("Saving tool:", toolData);

            // Create or update
            const response = id 
                ? await updateTool(id, toolData as Tool)
                : await createTool(toolData as Tool);

            if (response.error) throw new Error(response.error);

            setTool(response.data || null);
            setHasUnsavedChanges(false);
            
            // Update initial state references
            setInitialDetailsData(detailsData);
            setInitialConnectionData(connectionData);
            setInitialFunctionData(functionData);
            setInitialMessagesData(messagesData);
            setInitialKnowledgeBasesData(knowledgeBasesData);
            setInitialVoicemailData(voicemailData);

            // Navigate to tool detail page if newly created
            if (!id && response.data?.id) {
                const newToolId = response.data.id;
                
                // Set refs to prevent blockers from interfering (synchronous updates)
                isNavigatingAfterCreateRef.current = true;
                hasUnsavedChangesRef.current = false;
                
                // Show success toast
                successRef.current({
                    icon: CheckCircle2,
                    title: "Tool Created",
                    description: "Your new tool has been created",
                    duration: 3000,
                });
                
                // Clean up states
                setIsSaving(false);
                setHasUnsavedChanges(false);
                setBypassBlocker(true);
                
                // Small delay to ensure React processes ref updates before navigation
                requestAnimationFrame(() => {
                    navigate(`/dashboard/tools/${newToolId}`, { replace: true });
                });
                
                return; // Exit early - finally will still run but won't interfere
            }

            // For updates, show success toast immediately
            successRef.current({
                icon: CheckCircle2,
                title: "Tool Updated",
                description: "Your changes have been saved",
                duration: 3000,
            });
        } catch (error) {
            console.error("Error saving tool:", error);
            errorRef.current({
                icon: XCircle,
                title: "Save Failed",
                description: error instanceof Error ? error.message : "Failed to save tool",
                duration: 4000,
            });
        } finally {
            // Don't reset states if we're navigating after creating a tool
            if (!isNavigatingAfterCreateRef.current) {
                setIsSaving(false);
                setTimeout(() => {
                    setBypassBlocker(false);
                }, 300);
            }
        }
    };

    const handleDuplicate = async () => {
        if (!hasEditorAccess) {
            errorRef.current({
                icon: AlertCircle,
                title: "Permission Denied",
                description: "You need editor permissions to duplicate tools",
                duration: 4000,
            });
            return;
        }

        const proceedWithDuplicate = async () => {
            setIsDuplicating(true);
            setBypassBlocker(true); // Bypass blocker early
            try {
                // Gather all current form data
                const toolDataToDuplicate = {
                    details: detailsData,
                    connection: connectionData,
                    function: functionData, // Keep original name here, will be modified on new page
                    messages: messagesData,
                    destinations: tool?.destinations ?? []
                };

                // Navigate to the 'new' page, passing the data in state
                navigate('/dashboard/tools/new', {
                    replace: true, // Replace current entry to avoid back button issues
                    state: { duplicatedToolData: toolDataToDuplicate }
                });
                
                // Reset key after navigation to trigger remount/refresh if needed on the new page
                setKey(prev => prev + 1); // Re-enable this line

                // Show success message *after* navigation setup
                // Using a slight delay to ensure state update occurs before toast
                setTimeout(() => {
                     successRef.current({
                        icon: CheckCircle2,
                        title: "Tool Ready to Duplicate",
                        description: "Configuration copied. Review and save as a new tool.",
                        duration: 4000
                    });
                    setHasUnsavedChanges(false); // Reset unsaved changes only after successful navigation setup
                }, 100);


            } catch (err) {
                errorRef.current({
                    icon: AlertCircle,
                    title: "Duplication Setup Failed",
                    description: err instanceof Error ? err.message : "Failed to prepare tool duplication",
                    duration: 4000
                });
                setBypassBlocker(false); // Reset bypass blocker on error
            } finally {
                 // Set duplicating to false slightly later to allow navigation state to settle
                setTimeout(() => {
                    setIsDuplicating(false);
                }, 200);
            }
        };

        if (hasActualChanges() && !bypassBlocker) { // Check bypassBlocker here
            setShowUnsavedDialog(true);
            setPendingAction(() => proceedWithDuplicate);
        } else {
            await proceedWithDuplicate();
        }
    };

    useEffect(() => {
        if (bypassBlocker && !showUnsavedDialog && !pendingAction) {
            setBypassBlocker(false);
        }
    }, [location, showUnsavedDialog, pendingAction]);

    const handleExport = async () => {
        if (!id || !tool) {
            errorRef.current({
                icon: AlertCircle,
                title: "Export Failed",
                description: "Cannot export tool configuration. Tool data is not available.",
                duration: 4000,
            });
            return;
        }

        setIsExporting(true);
        try {
            const filename = downloadToolConfig(tool);
            successRef.current({
                icon: Download,
                title: "Configuration Exported",
                description: `Tool configuration saved as ${filename}`,
                duration: 4000
            });
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Export Failed",
                description: err instanceof Error ? err.message : "Failed to export configuration",
                duration: 4000
            });
        } finally {
            setIsExporting(false);
        }
    };

    const handleDelete = async () => {
        if (!id || !hasEditorAccess) {
            errorRef.current({
                icon: AlertCircle,
                title: "Permission Denied",
                description: "You need editor permissions to delete tools",
                duration: 4000,
            });
            return;
        }
        
        setIsDeleting(true);
        try {
            const response = await deleteTool(id);
            if (response.error) throw new Error(response.error);
            
            successRef.current({
                icon: CheckCircle2,
                title: "Tool Deleted",
                description: "Tool has been removed successfully",
                duration: 4000,
            });
            navigate("/dashboard/tools");
        } catch (err) {
            errorRef.current({
                icon: XCircle,
                title: "Delete Failed",
                description: err instanceof Error ? err.message : "Could not delete tool",
                duration: 4000,
            });
            setDeleteDialogOpen(false);
        } finally {
            setIsDeleting(false);
        }
    };

    if (isLoading) {
        return <ToolDetailsSkeleton />;
    }

    return (
        <div className="flex flex-col h-full">
            {/* Mobile Overlay */}
            <AnimatePresence>
                <MobileOverlay
                    icon={Wrench}
                    title="Desktop View Only"
                    description="Tool configuration is currently optimized for desktop view. Mobile support coming soon!"
                    onBack={() => navigate('/dashboard/tools')}
                />
            </AnimatePresence>

            {/* Main Content */}
            <div className="hidden sm:flex sm:flex-col sm:h-full">
                <SiteHeader
                    title={id ? "Tool Details" : "Create Tool"}
                    subtitle={id ? `${TYPE_LABELS[detailsData.type] || "Tool"} - Configure and manage your tool` : "Set up a new tool for your assistant"}
                    backButton={
                        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2">
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </Button>
                    }
                >
                    {/* Tool ID with FadeId Component */}
                    {id && (
                        <FadeId 
                            id={id} 
                            label="Tool ID"
                            tooltipSide="bottom"
                            height="h-9"
                        />
                    )}

                    {/* Action Buttons */}
                    {/* Export Button */}
                    {id && hasEditorAccess && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExport}
                            disabled={isExporting || !tool}
                            className="gap-2"
                        >
                            {isExporting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Exporting
                                </>
                            ) : (
                                <>
                                    <Download className="h-4 w-4" />
                                    Export
                                </>
                            )}
                        </Button>
                    )}

                    {/* Delete Button */}
                    {id && hasEditorAccess && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteDialogOpen(true)}
                            className="gap-2 text-destructive hover:text-destructive"
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete
                        </Button>
                    )}

                    {id && (
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleDuplicate}
                                            disabled={!hasEditorAccess || isDuplicating || isSaving}
                                            className="gap-2"
                                        >
                                            {isDuplicating ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Duplicating
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="h-4 w-4" />
                                                    Duplicate
                                                </>
                                            )}
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                {!hasEditorAccess && (
                                    <TooltipContent side="bottom" sideOffset={5} className="flex items-center gap-2 max-w-xs">
                                        <ShieldAlert className="w-12 h-12 text-amber-500" />
                                        <p>You need editor permissions to duplicate tools. Contact your administrator for access.</p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    )}

                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span>
                                    <Button
                                        size="sm"
                                        onClick={handleSave}
                                        disabled={!hasEditorAccess || isSaving || isDuplicating}
                                        className="gap-2"
                                    >
                                        {isSaving ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                {id ? 'Saving' : 'Creating'}
                                            </>
                                        ) : (
                                            <>
                                                <Save className="h-4 w-4" />
                                                {id ? 'Save' : 'Create'}
                                            </>
                                        )}
                                    </Button>
                                </span>
                            </TooltipTrigger>
                            {!hasEditorAccess && (
                                <TooltipContent side="bottom" sideOffset={5} className="flex items-center gap-2 max-w-xs">
                                    <ShieldAlert className="w-12 h-12 text-amber-500" />
                                    <p>You need editor permissions to {id ? 'edit' : 'create'} tools. Contact your administrator for access.</p>
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </TooltipProvider>
                </SiteHeader>

                <div className="flex-1 overflow-auto">
                    <AnimatePresence mode="wait">
                        <motion.div 
                            key={`tool-details-${key}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.15 }}
                            className="px-4 py-6 md:px-8 md:py-8"
                        >
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                                <TabsList className="h-11">
                                    {visibleTabs.filter(tab => tab.available).map(tab => (
                                        <TabsTrigger 
                                            key={tab.id}
                                            value={tab.id} 
                                            className="gap-2 px-4"
                                        >
                                            <tab.icon className="h-4 w-4" />
                                            {tab.label}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>

                                <TabsContent value="connection" className="m-0 mt-4">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {!visibleTabs.find(tab => tab.id === "connection")?.available ? (
                                            <div className="rounded-lg border bg-card p-6 text-center">
                                                <Webhook className="h-12 w-12 mx-auto text-muted-foreground/70 mb-2" />
                                                <h3 className="text-lg font-medium mb-1">Connection Not Available</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    Connection settings are not applicable for {TYPE_LABELS[detailsData.type || "function"]} tool type.
                                                </p>
                                            </div>
                                        ) : (
                                            <ConnectionForm
                                                value={connectionData}
                                                onChange={handleConnectionChange}
                                                isReadOnly={!hasEditorAccess}
                                            />
                                        )}
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="function" className="m-0 mt-4">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {!visibleTabs.find(tab => tab.id === "function")?.available ? (
                                            <div className="rounded-lg border bg-card p-6 text-center">
                                                <Hash className="h-12 w-12 mx-auto text-muted-foreground/70 mb-2" />
                                                <h3 className="text-lg font-medium mb-1">Function Not Available</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    Function settings are not applicable for {TYPE_LABELS[detailsData.type || "function"]} tool type.
                                                </p>
                                            </div>
                                        ) : (
                                            <SchemaForm
                                                value={functionData}
                                                onChange={handleFunctionChange}
                                                isReadOnly={!hasEditorAccess}
                                            />
                                        )}
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="messages" className="m-0 mt-4">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {!visibleTabs.find(tab => tab.id === "messages")?.available ? (
                                            <div className="rounded-lg border bg-card p-6 text-center">
                                                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/70 mb-2" />
                                                <h3 className="text-lg font-medium mb-1">Messages Not Available</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    Message settings are not applicable for {TYPE_LABELS[detailsData.type || "function"]} tool type.
                                                </p>
                                            </div>
                                        ) : (
                                            <MessagesForm
                                                value={messagesData}
                                                onChange={handleMessagesChange}
                                                isReadOnly={!hasEditorAccess}
                                            />
                                        )}
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="transfer" className="m-0 mt-4">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <TransferForm
                                            value={(tool?.destinations as TransferDestination[]) || []}
                                            onChange={(destinations) => {
                                                if (!tool || !hasEditorAccess) return;
                                                setTool({
                                                    ...tool,
                                                    destinations
                                                });
                                                handleFormUpdate();
                                            }}
                                            toolType={detailsData.type || "function"}
                                            isReadOnly={!hasEditorAccess}
                                        />
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="knowledgeBases" className="m-0 mt-4">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {!visibleTabs.find(tab => tab.id === "knowledgeBases")?.available ? (
                                            <div className="rounded-lg border bg-card p-6 text-center">
                                                <Book className="h-12 w-12 mx-auto text-muted-foreground/70 mb-2" />
                                                <h3 className="text-lg font-medium mb-1">QKnowledge Not Available</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    QKnowledge settings are only available for QKnowledge tool type.
                                                </p>
                                            </div>
                                        ) : (
                                            <KnowledgeBaseForm
                                                value={knowledgeBasesData}
                                                onChange={handleKnowledgeBasesChange}
                                                isReadOnly={!hasEditorAccess}
                                            />
                                        )}
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="voicemail" className="m-0 mt-4">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {!visibleTabs.find(tab => tab.id === "voicemail")?.available ? (
                                            <div className="rounded-lg border bg-card p-6 text-center">
                                                <Voicemail className="h-12 w-12 mx-auto text-muted-foreground/70 mb-2" />
                                                <h3 className="text-lg font-medium mb-1">Voicemail Not Available</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    Voicemail settings are only available for Voicemail tool type.
                                                </p>
                                            </div>
                                        ) : (
                                            <VoicemailForm
                                                value={voicemailData}
                                                onChange={handleVoicemailChange}
                                                isReadOnly={!hasEditorAccess}
                                            />
                                        )}
                                    </motion.div>
                                </TabsContent>

                                <TabsContent value="handoff" className="m-0 mt-4">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {!visibleTabs.find(tab => tab.id === "handoff")?.available ? (
                                            <div className="rounded-lg border bg-card p-6 text-center">
                                                <ArrowRightLeft className="h-12 w-12 mx-auto text-muted-foreground/70 mb-2" />
                                                <h3 className="text-lg font-medium mb-1">TransAssist Not Available</h3>
                                                <p className="text-sm text-muted-foreground">
                                                    TransAssist settings are only available for TransAssist tool type.
                                                </p>
                                            </div>
                                        ) : (
                                            <HandoffForm
                                                value={(tool?.destinations as HandoffDestination[]) || []}
                                                onChange={(destinations) => {
                                                    if (!tool || !hasEditorAccess) return;
                                                    setTool({
                                                        ...tool,
                                                        destinations
                                                    });
                                                    handleFormUpdate();
                                                }}
                                                isAsync={detailsData.isAsync}
                                                onAsyncChange={(isAsync) => {
                                                    if (!hasEditorAccess) return;
                                                    setDetailsData({
                                                        ...detailsData,
                                                        isAsync
                                                    });
                                                    handleFormUpdate();
                                                }}
                                                isReadOnly={!hasEditorAccess}
                                            />
                                        )}
                                    </motion.div>
                                </TabsContent>
                            </Tabs>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            <DeleteDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleDelete}
                toolName={tool?.function?.name || "Unnamed Tool"}
                isDeleting={isDeleting}
            />

            <AlertDialog 
                open={showUnsavedDialog} 
                onOpenChange={(open) => {
                    if (!open) {
                        handleCancel();
                    }
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel 
                            onClick={(e) => {
                                e.preventDefault();
                                handleCancel();
                            }}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={(e) => {
                                e.preventDefault();
                                if (pendingAction) {
                                    setBypassBlocker(true);
                                    pendingAction();
                                    setHasUnsavedChanges(false);
                                    setShowUnsavedDialog(false);
                                    setPendingAction(null);
                                }
                            }}
                        >
                            Leave Without Saving
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
